import type { SCProductData } from "@biz/sc/IShopData";
import type { ITransport } from "@biz/CommonLogic";
import type { Access } from "@biz/definitions/AccessDefinitions";
import type { ProjectType } from "@biz/definitions/AppDefinitions";
import type { RuleArticleMap } from "@biz/definitions/ArticleDefinitions";
import type { DiscountGroups } from "@biz/definitions/DatabaseDefinitions";
import type { ProductData3d } from "@biz/definitions/IVariantData3d";
import type { PointName, SunSailSides, SunSailNetPoints, SunSailNetSides } from "@biz/definitions/SunSailDefinitions";
import type { IFieldErrorMap } from "@biz/models/FieldErrorMap";
import type { INotesAndFilesItem } from "@biz/models/NotesAndFiles";
import type { IProduct2dForm } from "@biz/models/Sail2dDefinitions";
import type { IShoppingListContext } from "@biz/models/ShoppingListDefinitions";
import type { IProductDescriptor } from "@biz/ProductDescriptor";
import { FormatValues } from "@base/util/StringFormat";
import { ICurrencyData } from "@biz/definitions/PriceDefinitions";

type CurtainCloth = `Cloth_${number}_${number}`;

export type ProjectCheckErrorPosition = "project" | "form" | "product" | "articleAvailability" | "net" | "axle" | "group1" | "group2" | PointName | SunSailSides | SunSailNetPoints | SunSailNetSides | CurtainCloth;

export class ProjectCheckErrorPositionUtils {
 static isPoint(name: ProjectCheckErrorPosition | "all"): boolean {
  return name.length === 1;
 }
 static isEdge(name: ProjectCheckErrorPosition | "all"): boolean {
  return name.length === 2;
 }
}

export enum ProjectCheckErrorType {
  None = 0,
  Hint = 200,
  Warning = 300,
  Error = 400,
  FatalError = 500,
}

export type IProjectCheckErrorFieldGroup = "form" | "sunSail" | "shoppingCart";

export interface IProjectCheckErrorField {
  id: string;
  group: IProjectCheckErrorFieldGroup;
}

export interface IProjectCheckError {
  position: ProjectCheckErrorPosition;
  type: ProjectCheckErrorType;
  text: string;
  fields: IProjectCheckErrorField[];
  v: 2;
  errorUrl?: string;
}

export interface IProjectCheckErrorOptions {
  params?: FormatValues;
  errorUrl?: string;
}

export enum ProjectInfoPriority {
  SunSail,
  IO,
  Other,
}

export type IProjectInfoValue = string | { header: string, value: string }[];

export interface IProjectInfo {
  name: string;
  value: IProjectInfoValue;
  priority: ProjectInfoPriority;
  access: Access;
  group?: string;
  maxPerRow?: number;
}

export interface ICalcContext extends IShoppingListContext {
  errors: IProjectCheckError[];
  infos: IProjectInfo[];
}


export interface ICalculatedBaseProduct {
  articles: RuleArticleMap;
  errors: IProjectCheckError[];
  errorMap: IFieldErrorMap<IProjectCheckErrorFieldGroup>;
  infos: IProjectInfo[];
}

export interface ICalculatedShoppingCart extends ICalculatedBaseProduct {
  notesAndFiles: INotesAndFilesItem[];
}

export interface ICalculatedOfferDataResult {
  errors: IProjectCheckError[];
  errorMap: IFieldErrorMap<IProjectCheckErrorFieldGroup>;
  customerSurcharge: number;
  discountOverrides: DiscountGroups | undefined;

  discountGroups: DiscountGroups; // calculated
  inflationSurchargePercent: number; // calculated
  orderTime: number; // calculated
}

export type ProductData = ProductData3d | IProduct2dForm | SCProductData;

export interface ICalculatedProductResult extends ICalculatedBaseProduct {
  id: string;
  pd: IProductDescriptor,
  // modified: M; // this is the form passed through calculate - in same rare cases it gets updated inside and a new instance is returned
  product: ProductData | undefined;
}

export interface ICalculatedProjectResult {
  type: ProjectType;
  name: string;
  products: Record<string, ICalculatedProductResult>;
  shoppingCart: ICalculatedShoppingCart;
  offer: ICalculatedOfferDataResult;
  currency: ICurrencyData;
  transport: ITransport | undefined;
  errors?: IProjectCheckError[];
}

export function getCalculatedProductIndex(products: Record<string, ICalculatedProductResult> | undefined, id: string): number {
  if (!products)
    return -1;
  return Object.keys(products).indexOf(id);
}